import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Target, Plus, ChevronDown, ChevronRight, Link2, Unlink, Building2,
  TrendingUp, Calendar, Users, Pencil, Trash2, FileText, Layers,
  BarChart3, CheckCircle2, AlertCircle, ArrowRight, Zap
} from 'lucide-react';
import { API_URL } from '../../config';

const AOP_API = API_URL + '/aop';
const PERF_API = API_URL + '/performance';

const deptColors = {
  'HR': { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', badge: 'bg-violet-100 text-violet-800', accent: 'bg-violet-500' },
  'Accounts': { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800', accent: 'bg-emerald-500' },
  'Purchase': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800', accent: 'bg-amber-500' },
  'Production': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800', accent: 'bg-blue-500' },
  'Sales': { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-800', accent: 'bg-rose-500' },
};

const getDeptColor = (name) => {
  const key = Object.keys(deptColors).find(k => name?.toLowerCase().includes(k.toLowerCase()));
  return deptColors[key] || { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', badge: 'bg-slate-100 text-slate-800', accent: 'bg-slate-500' };
};

const AopTab = ({ authHeaders, employees }) => {
  const [plan, setPlan] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [allKpis, setAllKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDept, setExpandedDept] = useState(null);
  const [expandedObj, setExpandedObj] = useState(null);

  // Dialogs
  const [showObjDialog, setShowObjDialog] = useState(false);
  const [editingObj, setEditingObj] = useState(null);
  const [objForm, setObjForm] = useState({});
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkingObjId, setLinkingObjId] = useState(null);
  const [linkSearch, setLinkSearch] = useState('');
  const [showSeedConfirm, setShowSeedConfirm] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [autoLinking, setAutoLinking] = useState(false);
  const [viewMode, setViewMode] = useState('manage'); // 'manage' or 'scorecard'
  const [scorecard, setScorecard] = useState(null);
  const [scorecardQuarter, setScorecardQuarter] = useState('');
  const [scorecardLoading, setScorecardLoading] = useState(false);

  // Department list from DB
  const [dbDepartments, setDbDepartments] = useState([]);

  const hdrs = { headers: authHeaders };
  const jsonHdrs = { headers: { 'Content-Type': 'application/json', ...authHeaders } };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [progressR, kpiR, deptR] = await Promise.all([
        fetch(`${AOP_API}/progress`, hdrs),
        fetch(`${PERF_API}/all-kpi-definitions`, hdrs),
        fetch(`${API_URL}/departments`, hdrs),
      ]);
      if (progressR.ok) {
        const data = await progressR.json();
        setPlan(data.plan);
        setDepartments(data.departments || []);
        setSummary(data.summary);
      }
      if (kpiR.ok) setAllKpis(await kpiR.json());
      if (deptR.ok) setDbDepartments(await deptR.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const seedAop = async () => {
    setSeeding(true);
    try {
      const r = await fetch(`${AOP_API}/seed`, { method: 'POST', ...jsonHdrs });
      if (r.ok) {
        const d = await r.json();
        toast.success(d.message);
        setShowSeedConfirm(false);
        fetchData();
      } else {
        const err = await r.json();
        toast.error(err.detail || 'Failed to seed');
      }
    } catch { toast.error('Error seeding AOP'); }
    finally { setSeeding(false); }
  };

  const autoLinkKpis = async () => {
    if (!window.confirm('Auto-link matching KPI assignments across their departments, effective today? Existing employee/shared scopes stay unchanged.')) return;
    setAutoLinking(true);
    try {
      const r = await fetch(`${AOP_API}/auto-link`, { method: 'POST', ...jsonHdrs, body: JSON.stringify({ confirmed_scope: true }) });
      if (r.ok) {
        const d = await r.json();
        toast.success(d.message);
        fetchData();
      } else toast.error('Auto-link failed');
    } catch { toast.error('Error auto-linking'); }
    finally { setAutoLinking(false); }
  };

  const fetchScorecard = async (q = scorecardQuarter) => {
    setScorecardLoading(true);
    try {
      const qs = q ? `?quarter=${q}` : '';
      const r = await fetch(`${AOP_API}/scorecard${qs}`, hdrs);
      if (r.ok) {
        const data = await r.json();
        setScorecard(data);
        if (!q && data.quarter) setScorecardQuarter(data.quarter);
      }
    } catch (e) { console.error(e); }
    finally { setScorecardLoading(false); }
  };

  const saveObjective = async () => {
    try {
      const payload = { ...objForm, plan_id: plan?.plan_id };
      const url = editingObj
        ? `${AOP_API}/objectives/${editingObj.objective_id}`
        : `${AOP_API}/objectives`;
      const method = editingObj ? 'PUT' : 'POST';
      const r = await fetch(url, { method, ...jsonHdrs, body: JSON.stringify(payload) });
      if (r.ok) {
        toast.success(editingObj ? 'Objective updated' : 'Objective created');
        setShowObjDialog(false);
        setEditingObj(null);
        setObjForm({});
        fetchData();
      } else toast.error('Failed to save');
    } catch { toast.error('Error saving objective'); }
  };

  const deleteObjective = async (objId) => {
    try {
      const r = await fetch(`${AOP_API}/objectives/${objId}`, { method: 'DELETE', ...hdrs });
      if (r.ok) { toast.success('Objective removed'); fetchData(); }
    } catch { toast.error('Error'); }
  };

  const linkKpi = async (kpiId, objectiveId) => {
    if (!window.confirm('Link this KPI in its existing employee/shared scope, effective today? Earlier submissions remain unchanged.')) return;
    try {
      const r = await fetch(`${AOP_API}/link-kpi`, {
        method: 'POST', ...jsonHdrs,
        body: JSON.stringify({ kpi_id: kpiId, objective_id: objectiveId, confirmed_scope: true })
      });
      if (r.ok) { toast.success('KPI linked to objective'); fetchData(); }
      else { const d = await r.json(); toast.error(d.detail || 'Link failed'); }
    } catch { toast.error('Error linking'); }
  };

  const unlinkKpi = async (kpiId) => {
    if (!window.confirm('Unlink this KPI in its existing employee/shared scope, effective today? Earlier submissions remain unchanged.')) return;
    try {
      const r = await fetch(`${AOP_API}/unlink-kpi`, {
        method: 'POST', ...jsonHdrs,
        body: JSON.stringify({ kpi_id: kpiId, confirmed_scope: true })
      });
      if (r.ok) { toast.success('KPI unlinked'); fetchData(); }
      else { const d = await r.json(); toast.error(d.detail || 'Unlink failed'); }
    } catch { toast.error('Error unlinking'); }
  };

  const openEditObj = (obj) => {
    setEditingObj(obj);
    setObjForm({
      objective_name: obj.objective_name,
      strategy: obj.strategy,
      annual_target: obj.annual_target,
      q1_target: obj.q1_target,
      q2_target: obj.q2_target,
      q3_target: obj.q3_target,
      q4_target: obj.q4_target,
      department_id: obj.department_id,
      department_name: obj.department_name,
      owner_name: obj.owner_name,
      weightage: obj.weightage,
    });
    setShowObjDialog(true);
  };

  const openNewObj = (deptId, deptName) => {
    setEditingObj(null);
    setObjForm({ department_id: deptId, department_name: deptName });
    setShowObjDialog(true);
  };

  const openLinkDialog = (objId) => {
    setLinkingObjId(objId);
    setLinkSearch('');
    setShowLinkDialog(true);
  };

  // KPIs available for linking (not already linked to an objective, or linked to the current one)
  const getAvailableKpis = () => {
    const q = linkSearch.toLowerCase();
    return allKpis.filter(k => {
      const matchesSearch = !q || k.name?.toLowerCase().includes(q) || k.employee_name?.toLowerCase().includes(q);
      const notLinked = !k.aop_objective_id || k.aop_objective_id === linkingObjId;
      return matchesSearch && notLinked;
    });
  };

  // Get KPIs already linked to the current linking objective
  const getLinkedKpisForObj = (objId) => {
    return allKpis.filter(k => k.aop_objective_id === objId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-slate-200 border-t-blue-500 rounded-full" />
      </div>
    );
  }

  // No plan yet - show seed button
  if (!plan) {
    return (
      <Card className="border-dashed border-2 border-slate-300" data-testid="aop-no-plan">
        <CardContent className="py-16 text-center">
          <Layers className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No Annual Operating Plan Found</h3>
          <p className="text-slate-500 mb-6 max-w-md mx-auto">
            Initialize the AOP from your department Excel sheets. This will create the plan structure with all 5 department objectives (HR, Accounts, Purchase, Production, Sales).
          </p>
          <Button onClick={() => setShowSeedConfirm(true)} className="gap-2" data-testid="seed-aop-btn">
            <Zap className="w-4 h-4" /> Initialize AOP FY 2026-27
          </Button>

          <Dialog open={showSeedConfirm} onOpenChange={setShowSeedConfirm}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Initialize Annual Operating Plan</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-600">
                This will create the AOP structure with 34 objectives across 5 departments based on your Excel sheets.
                You can then link existing KPIs to each objective.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSeedConfirm(false)}>Cancel</Button>
                <Button onClick={seedAop} disabled={seeding} data-testid="confirm-seed-btn">
                  {seeding ? 'Initializing...' : 'Initialize'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="aop-tab">
      {/* Plan Header */}
      <Card data-testid="aop-plan-header">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                {plan.name}
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">FY {plan.fiscal_year} • {plan.status}</p>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex border rounded-md overflow-hidden">
                <button
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'manage' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  onClick={() => setViewMode('manage')}
                  data-testid="view-manage-btn"
                >Manage</button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'scorecard' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  onClick={() => { setViewMode('scorecard'); if (!scorecard) fetchScorecard(); }}
                  data-testid="view-scorecard-btn"
                >Scorecard</button>
              </div>
              {viewMode === 'manage' && (
                <Button size="sm" variant="outline" onClick={autoLinkKpis} disabled={autoLinking}
                  className="gap-1 text-xs" data-testid="auto-link-btn">
                  <Zap className="w-3.5 h-3.5" /> {autoLinking ? 'Linking...' : 'Auto-Link KPIs'}
                </Button>
              )}
              <Badge variant="outline" className="text-xs">
                {summary?.total_departments || 0} Depts
              </Badge>
              <Badge variant="outline" className="text-xs">
                {summary?.total_objectives || 0} Obj
              </Badge>
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                {summary?.total_linked_kpis || 0} KPIs
              </Badge>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* ===== SCORECARD VIEW ===== */}
      {viewMode === 'scorecard' && (
        <ScorecardView
          scorecard={scorecard}
          loading={scorecardLoading}
          quarter={scorecardQuarter}
          onQuarterChange={(q) => { setScorecardQuarter(q); fetchScorecard(q); }}
          authHeaders={authHeaders}
        />
      )}

      {/* ===== MANAGE VIEW ===== */}
      {viewMode === 'manage' && (<>

      {/* Department Cards */}
      {departments.map((dept) => {
        const colors = getDeptColor(dept.department_name);
        const isExpanded = expandedDept === dept.department_id;

        return (
          <Card
            key={dept.department_id}
            className={`border ${colors.border} overflow-hidden`}
            data-testid={`dept-card-${dept.department_id}`}
          >
            {/* Department Header */}
            <div
              className={`${colors.bg} px-5 py-4 cursor-pointer flex items-center justify-between`}
              onClick={() => setExpandedDept(isExpanded ? null : dept.department_id)}
              data-testid={`dept-toggle-${dept.department_id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-1.5 h-10 rounded-full ${colors.accent}`} />
                <div>
                  <h3 className={`font-semibold ${colors.text}`}>
                    <Building2 className="w-4 h-4 inline mr-1.5" />
                    {dept.department_name}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {dept.objectives?.length || 0} objectives • {dept.linked_kpis || 0} linked KPIs
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  size="sm"
                  variant="ghost"
                  className={`text-xs ${colors.text}`}
                  onClick={(e) => { e.stopPropagation(); openNewObj(dept.department_id, dept.department_name); }}
                  data-testid={`add-obj-${dept.department_id}`}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Objective
                </Button>
                {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
              </div>
            </div>

            {/* Objectives List */}
            {isExpanded && (
              <CardContent className="pt-4 space-y-3">
                {dept.objectives?.map((obj) => {
                  const linkedKpis = obj.kpis || [];
                  const isObjExpanded = expandedObj === obj.objective_id;

                  return (
                    <div
                      key={obj.objective_id}
                      className="border border-slate-200 rounded-lg overflow-hidden"
                      data-testid={`obj-card-${obj.objective_id}`}
                    >
                      {/* Objective Header */}
                      <div
                        className="px-4 py-3 bg-white hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                        onClick={() => setExpandedObj(isObjExpanded ? null : obj.objective_id)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-slate-800 truncate">
                              {obj.objective_name}
                            </span>
                            {obj.weightage > 0 && (
                              <Badge variant="outline" className="text-[10px] shrink-0">
                                {obj.weightage}% wt
                              </Badge>
                            )}
                            <Badge className={`text-[10px] shrink-0 ${linkedKpis.length > 0 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                              {linkedKpis.length} KPIs
                            </Badge>
                          </div>
                          {obj.strategy && (
                            <p className="text-xs text-slate-500 mt-0.5 truncate">{obj.strategy}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 ml-3 shrink-0">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); openLinkDialog(obj.objective_id); }}
                            title="Link KPIs" data-testid={`link-kpi-btn-${obj.objective_id}`}>
                            <Link2 className="w-3.5 h-3.5 text-blue-500" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); openEditObj(obj); }}
                            data-testid={`edit-obj-${obj.objective_id}`}>
                            <Pencil className="w-3.5 h-3.5 text-slate-400" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); deleteObjective(obj.objective_id); }}
                            data-testid={`delete-obj-${obj.objective_id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </Button>
                          {isObjExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        </div>
                      </div>

                      {/* Objective Details (expanded) */}
                      {isObjExpanded && (
                        <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 space-y-3">
                          {/* Targets Grid */}
                          <div className="grid grid-cols-5 gap-2">
                            <div className="bg-white p-2 rounded border border-slate-200 text-center">
                              <p className="text-[10px] text-slate-400 uppercase">Annual</p>
                              <p className="text-xs font-medium text-slate-700 mt-0.5">{obj.annual_target || '—'}</p>
                            </div>
                            {['Q1', 'Q2', 'Q3', 'Q4'].map((q, i) => (
                              <div key={q} className="bg-white p-2 rounded border border-slate-200 text-center">
                                <p className="text-[10px] text-slate-400 uppercase">{q}</p>
                                <p className="text-xs font-medium text-slate-700 mt-0.5">
                                  {obj[`q${i+1}_target`] || '—'}
                                </p>
                              </div>
                            ))}
                          </div>

                          {obj.owner_name && (
                            <div className="flex items-center gap-1.5 text-xs text-slate-500">
                              <Users className="w-3.5 h-3.5" /> Owner: <span className="font-medium text-slate-700">{obj.owner_name}</span>
                            </div>
                          )}

                          {/* Linked KPIs */}
                          {linkedKpis.length > 0 ? (
                            <div className="space-y-1.5">
                              <p className="text-xs font-medium text-slate-600">Linked KPIs:</p>
                              {linkedKpis.map(kpi => (
                                <div key={kpi.kpi_id} className="flex items-center justify-between bg-white px-3 py-2 rounded border border-slate-200">
                                  <div>
                                    <span className="text-xs font-medium text-slate-700">{kpi.name}</span>
                                    {kpi.employee_name && (
                                      <span className="text-[10px] text-slate-400 ml-2">({kpi.employee_name})</span>
                                    )}
                                    {kpi.target_value && (
                                      <Badge variant="outline" className="text-[10px] ml-2">Target: {kpi.target_value}{kpi.unit}</Badge>
                                    )}
                                  </div>
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => unlinkKpi(kpi.kpi_id)}
                                    data-testid={`unlink-kpi-${kpi.kpi_id}`}>
                                    <Unlink className="w-3 h-3 text-red-400" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-3 bg-white rounded border border-dashed border-slate-300">
                              <AlertCircle className="w-4 h-4 mx-auto text-orange-400 mb-1" />
                              <p className="text-xs text-slate-500">No KPIs linked yet</p>
                              <Button size="sm" variant="link" className="text-xs mt-1 h-auto p-0" onClick={() => openLinkDialog(obj.objective_id)}>
                                Link KPIs →
                              </Button>
                            </div>
                          )}

                          {/* Suggested KPI names from seed data */}
                          {obj.kpi_names?.length > 0 && (
                            <div className="text-xs text-slate-500">
                              <span className="font-medium">Suggested KPIs from AOP:</span>{' '}
                              {obj.kpi_names.join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {(!dept.objectives || dept.objectives.length === 0) && (
                  <div className="text-center py-8 text-slate-400 text-sm">
                    No objectives defined for this department
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* Objective Create/Edit Dialog */}
      </>)}

      <Dialog open={showObjDialog} onOpenChange={setShowObjDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingObj ? 'Edit Objective' : 'New Objective'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Department</Label>
              <Select value={objForm.department_id || ''} onValueChange={v => {
                const d = dbDepartments.find(x => x.department_id === v);
                setObjForm(f => ({ ...f, department_id: v, department_name: d?.name || '' }));
              }}>
                <SelectTrigger data-testid="obj-dept-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  {dbDepartments.map(d => (
                    <SelectItem key={d.department_id} value={d.department_id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Objective Name *</Label>
              <Input value={objForm.objective_name || ''} onChange={e => setObjForm(f => ({ ...f, objective_name: e.target.value }))}
                placeholder="e.g., Cost Reduction" data-testid="obj-name-input" />
            </div>
            <div>
              <Label className="text-xs">Strategy</Label>
              <Textarea value={objForm.strategy || ''} onChange={e => setObjForm(f => ({ ...f, strategy: e.target.value }))}
                placeholder="How will this objective be achieved?" rows={2} data-testid="obj-strategy-input" />
            </div>
            <div>
              <Label className="text-xs">Annual Target</Label>
              <Input value={objForm.annual_target || ''} onChange={e => setObjForm(f => ({ ...f, annual_target: e.target.value }))}
                placeholder="e.g., 100% compliance" data-testid="obj-annual-target" />
            </div>
            <div className="grid grid-cols-4 gap-2">
              {['Q1', 'Q2', 'Q3', 'Q4'].map((q, i) => (
                <div key={q}>
                  <Label className="text-[10px]">{q} Target</Label>
                  <Input value={objForm[`q${i+1}_target`] || ''} onChange={e => setObjForm(f => ({ ...f, [`q${i+1}_target`]: e.target.value }))}
                    placeholder={q} className="text-xs" />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Owner</Label>
                <Input value={objForm.owner_name || ''} onChange={e => setObjForm(f => ({ ...f, owner_name: e.target.value }))}
                  placeholder="e.g., HR Head" />
              </div>
              <div>
                <Label className="text-xs">Weightage (%)</Label>
                <Input type="number" value={objForm.weightage || ''} onChange={e => setObjForm(f => ({ ...f, weightage: Number(e.target.value) }))}
                  placeholder="0" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowObjDialog(false); setEditingObj(null); }}>Cancel</Button>
            <Button onClick={saveObjective} disabled={!objForm.objective_name} data-testid="save-obj-btn">
              {editingObj ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link KPI Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-blue-500" /> Link KPIs to Objective
            </DialogTitle>
          </DialogHeader>

          {/* Already linked */}
          {linkingObjId && getLinkedKpisForObj(linkingObjId).length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium text-slate-600 mb-1.5">Currently Linked:</p>
              <div className="space-y-1">
                {getLinkedKpisForObj(linkingObjId).map(kpi => (
                  <div key={kpi.kpi_id} className="flex items-center justify-between bg-green-50 px-3 py-1.5 rounded border border-green-200">
                    <span className="text-xs text-green-800">{kpi.name} {kpi.employee_name ? `(${kpi.employee_name})` : ''}</span>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-500" onClick={() => unlinkKpi(kpi.kpi_id)}>
                      Unlink
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Input
            placeholder="Search KPIs by name or employee..."
            value={linkSearch}
            onChange={e => setLinkSearch(e.target.value)}
            className="text-sm"
            data-testid="link-kpi-search"
          />
          <div className="flex-1 overflow-y-auto mt-2 space-y-1 min-h-0" style={{ maxHeight: '400px' }}>
            {getAvailableKpis().slice(0, 50).map(kpi => {
              const isLinked = kpi.aop_objective_id === linkingObjId;
              return (
                <div
                  key={kpi.kpi_id}
                  className={`flex items-center justify-between px-3 py-2 rounded border ${isLinked ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-slate-700 truncate">{kpi.name}</p>
                    <p className="text-[10px] text-slate-400">
                      {kpi.employee_name || 'No employee'} • {kpi.calculation_type} • Target: {kpi.target_value}
                    </p>
                  </div>
                  {isLinked ? (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-red-500 border-red-200" onClick={() => unlinkKpi(kpi.kpi_id)}>
                      <Unlink className="w-3 h-3 mr-1" /> Unlink
                    </Button>
                  ) : (
                    <Button size="sm" className="h-7 text-xs" onClick={() => linkKpi(kpi.kpi_id, linkingObjId)}
                      data-testid={`link-kpi-${kpi.kpi_id}`}>
                      <Link2 className="w-3 h-3 mr-1" /> Link
                    </Button>
                  )}
                </div>
              );
            })}
            {getAvailableKpis().length === 0 && (
              <p className="text-center text-sm text-slate-400 py-6">No matching KPIs found</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const statusColors = {
  on_track: { bg: 'bg-emerald-100', text: 'text-emerald-800', dot: 'bg-emerald-500' },
  at_risk: { bg: 'bg-amber-100', text: 'text-amber-800', dot: 'bg-amber-500' },
  off_track: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
  no_data: { bg: 'bg-slate-100', text: 'text-slate-500', dot: 'bg-slate-400' },
};

const ScorecardView = ({ scorecard, loading, quarter, onQuarterChange }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin w-6 h-6 border-4 border-slate-200 border-t-blue-500 rounded-full" />
      </div>
    );
  }

  if (!scorecard?.plan) {
    return <p className="text-center text-slate-400 py-8">No scorecard data available</p>;
  }

  return (
    <div className="space-y-4" data-testid="aop-scorecard">
      {/* Quarter Selector + Company Score */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <Select value={quarter} onValueChange={onQuarterChange}>
            <SelectTrigger className="w-28 h-8 text-xs" data-testid="scorecard-quarter-select">
              <SelectValue placeholder="Quarter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Q1">Q1 (Apr-Jun)</SelectItem>
              <SelectItem value="Q2">Q2 (Jul-Sep)</SelectItem>
              <SelectItem value="Q3">Q3 (Oct-Dec)</SelectItem>
              <SelectItem value="Q4">Q4 (Jan-Mar)</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-400">
            {scorecard.from_date} to {scorecard.to_date}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[10px] uppercase text-slate-400 tracking-wider">Company Score</p>
            <p className={`text-2xl font-bold ${scorecard.company_score >= 70 ? 'text-emerald-600' : scorecard.company_score >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
              {scorecard.company_score == null ? 'Unavailable' : `${scorecard.company_score}%`}
            </p>
          </div>
        </div>
      </div>

      {/* Department Scorecards */}
      {scorecard.departments?.map((dept) => {
        const colors = getDeptColor(dept.department_name);
        const sc = statusColors[dept.overall_progress >= 70 ? 'on_track' : dept.overall_progress >= 40 ? 'at_risk' : 'off_track'];

        return (
          <Card key={dept.department_id} className={`border ${colors.border} overflow-hidden`}>
            <div className={`${colors.bg} px-5 py-3 flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <div className={`w-1.5 h-8 rounded-full ${colors.accent}`} />
                <div>
                  <h3 className={`font-semibold text-sm ${colors.text}`}>
                    <Building2 className="w-4 h-4 inline mr-1" />
                    {dept.department_name}
                  </h3>
                  <p className="text-[10px] text-slate-500">{dept.objectives?.length} objectives</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-[10px] text-slate-400">Progress</p>
                  <p className={`text-lg font-bold ${dept.overall_progress >= 70 ? 'text-emerald-600' : dept.overall_progress >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                    {dept.overall_progress == null ? 'Unavailable' : `${dept.overall_progress}%`}
                  </p>
                </div>
                <div className="w-20">
                  <Progress value={Math.min(100, dept.overall_progress)} className="h-2" />
                </div>
              </div>
            </div>

            <CardContent className="pt-3 pb-4 space-y-2">
              {dept.objectives?.map((obj) => {
                const sc = statusColors[obj.status] || statusColors.no_data;
                return (
                  <div key={obj.objective_id} className="border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${sc.dot}`} />
                        <span className="text-xs font-medium text-slate-800 truncate">{obj.objective_name}</span>
                        {obj.weightage > 0 && <Badge variant="outline" className="text-[10px] shrink-0">{obj.weightage}%</Badge>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <Badge className={`text-[10px] ${sc.bg} ${sc.text}`}>
                          {obj.status === 'no_data' ? 'No Data' : obj.status === 'on_track' ? 'On Track' : obj.status === 'at_risk' ? 'At Risk' : 'Off Track'}
                        </Badge>
                        <span className={`text-sm font-bold ${obj.avg_score >= 70 ? 'text-emerald-600' : obj.avg_score >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                          {obj.avg_score == null ? 'Unavailable' : `${obj.avg_score}%`}
                        </span>
                      </div>
                    </div>

                    {obj.quarter_target && (
                      <p className="text-[10px] text-slate-400 mb-1.5">Target: {obj.quarter_target}</p>
                    )}

                    {/* KPI Score Bars */}
                    {obj.kpi_scores?.length > 0 && (
                      <div className="space-y-1 mt-2">
                        {obj.kpi_scores.map(kpi => (
                          <div key={kpi.kpi_id} className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-500 w-32 truncate shrink-0" title={kpi.name}>{kpi.name}</span>
                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${kpi.score_percentage >= 70 ? 'bg-emerald-500' : kpi.score_percentage >= 40 ? 'bg-amber-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.min(100, kpi.score_percentage)}%` }}
                              />
                            </div>
                            <span data-testid={`aop-kpi-score-${kpi.kpi_id}-${kpi.revision_id}`} className="text-[10px] font-medium text-slate-600 min-w-[64px] text-right">{kpi.score_percentage == null ? 'Unavailable' : `${kpi.score_percentage}%`}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {obj.kpi_count === 0 && (
                      <p className="text-[10px] text-slate-400 italic">No KPIs linked</p>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default AopTab;
